import Modal from "components/DesignSystem/Modal/Modal";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useToast } from "hooks/useToast";
import { PhysicalNexusTrackerModalBody } from "./PhysicalNexusTrackerModalBody";
import { Form, Formik } from "formik";
import {
  PhysicalNexusTracker,
  useGetPhysicalNexusTrackerQuery,
  useUpdatePhysicalNexusTrackerMutation,
} from "store/apis/salesTax";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { BackendError } from "types/utils/error";
import { useGetCountriesDataQuery } from "store/apis/onboarding";
import { US } from "constants/countryCodes";
import { Button } from "components/DesignSystem/Button/Button";

export type PhysicalNexusStateRowFormValues = {
  state: string;
  office: boolean;
  warehouse: boolean;
  employee: boolean;
};

type PhysicalNexusTrackerFormValues = {
  states: PhysicalNexusStateRowFormValues[];
};

const EMPTY_STATE_ROW: PhysicalNexusStateRowFormValues = {
  state: "",
  office: false,
  warehouse: false,
  employee: false,
};

const toStateRows = (
  physicalNexusData?: PhysicalNexusTracker
): PhysicalNexusStateRowFormValues[] => {
  if (!physicalNexusData) {
    return [];
  }

  const officeStates = new Set(physicalNexusData.office_in_us);
  const warehouseStates = new Set(physicalNexusData.warehouse_in_us);
  const employeeStates = new Set(physicalNexusData.employees_in_us);
  const allStates = new Set([
    ...officeStates,
    ...warehouseStates,
    ...employeeStates,
  ]);

  return Array.from(allStates).map((stateId) => ({
    state: stateId,
    office: officeStates.has(stateId),
    warehouse: warehouseStates.has(stateId),
    employee: employeeStates.has(stateId),
  }));
};

const toPhysicalNexusPayload = (
  editedRows: PhysicalNexusStateRowFormValues[],
  physicalNexusData?: PhysicalNexusTracker
): PhysicalNexusTracker => {
  const filledEditedRows = editedRows.filter((row) => row.state);
  const editedStateIds = new Set(filledEditedRows.map((row) => row.state));

  // Merge the edited rows back into the entity's other existing states —
  // the modal only ever shows a subset of states (the one being edited, or
  // newly added ones), so submitting must not wipe out states not shown.
  const otherRows = toStateRows(physicalNexusData).filter(
    (row) => !editedStateIds.has(row.state)
  );

  const allRows = [...otherRows, ...filledEditedRows];
  const officeStates = allRows.filter((row) => row.office).map((r) => r.state);
  const warehouseStates = allRows
    .filter((row) => row.warehouse)
    .map((r) => r.state);
  const employeeStates = allRows
    .filter((row) => row.employee)
    .map((r) => r.state);

  return {
    office_in_us: officeStates,
    warehouse_in_us: warehouseStates,
    employees_in_us: employeeStates,
    office_present: officeStates.length > 0,
    warehouse_present: warehouseStates.length > 0,
    employees_present: employeeStates.length > 0,
  };
};

const toInitialStateRows = (
  physicalNexusData?: PhysicalNexusTracker,
  scopedStateId?: string | null
): PhysicalNexusStateRowFormValues[] => {
  if (!scopedStateId) {
    return [{ ...EMPTY_STATE_ROW }];
  }

  const scopedRow = toStateRows(physicalNexusData).find(
    (row) => row.state === scopedStateId
  );

  return [scopedRow || { ...EMPTY_STATE_ROW, state: scopedStateId }];
};

export const PhysicalNexusTrackerModal = ({
  onClose,
  isOpen,
  hasAlreadyCheckedNexus,
  stateId,
}: {
  isOpen: boolean;
  onClose: () => void;
  hasAlreadyCheckedNexus: boolean;
  stateId?: string | null;
}) => {
  const { alertToast, successToast } = useToast();
  const entityId = useCurrentEntityId();
  const [
    updatePhysicalNexusTrackerData,
    { isLoading: isUpdatingPhysicalNexus },
  ] = useUpdatePhysicalNexusTrackerMutation();

  const { data: physicalNexusData, isLoading: isPhysicalNexusLoading } =
    useGetPhysicalNexusTrackerQuery({ entityId }, { skip: !isOpen });

  const handleSubmit = async (values: PhysicalNexusTrackerFormValues) => {
    try {
      await updatePhysicalNexusTrackerData({
        entityId,
        payload: toPhysicalNexusPayload(values.states, physicalNexusData),
      }).unwrap();
      successToast({
        message: "Physical nexus details updated",
      });
      onClose();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const { data: countries, isLoading: isCountryLoading } =
    useGetCountriesDataQuery();

  const unitatedStates = countries?.find(
    (country) => country.code_alpha_2 === US
  )?.states;

  const stateOptions = unitatedStates?.map((state) => ({
    label: state.name,
    value: state.uuid,
  }));

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Modal.Content size="large" useCustomOverlay>
        <Modal.Header className="sm:t-pl-4">
          <Modal.Title>Update physical presence</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Async.Root
          isLoading={isCountryLoading || isPhysicalNexusLoading}
          isSuccess={!isCountryLoading && !isPhysicalNexusLoading}
          isEmpty={false}
        >
          <Async.Success>
            <Formik
              onSubmit={handleSubmit}
              enableReinitialize
              initialValues={{
                states: toInitialStateRows(physicalNexusData, stateId),
              }}
            >
              {({ values }) => {
                const hasIncompleteRow = values.states.some(
                  (row) => !row.state
                );

                return (
                  <Form className="t-flex t-flex-col t-min-h-0 t-flex-1">
                    <PhysicalNexusTrackerModalBody
                      stateOptions={stateOptions}
                      lockStateSelection={Boolean(stateId)}
                    />

                    <Modal.FooterButtonGroup>
                      <Button
                        customType="secondary"
                        type="button"
                        onClick={onClose}
                      >
                        Cancel
                      </Button>
                      <Button
                        customType="primary"
                        isLoading={isUpdatingPhysicalNexus}
                        disabled={hasIncompleteRow}
                      >
                        Confirm
                      </Button>
                    </Modal.FooterButtonGroup>
                  </Form>
                );
              }}
            </Formik>
          </Async.Success>
        </Async.Root>
      </Modal.Content>
    </Modal.Root>
  );
};
